import { GitCompareArrows, History, PanelRightClose, RotateCcw, RotateCw, User } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { ChangesList } from './ChangesList';
import { HistoryList } from './HistoryList';
import { PersonDetails } from './PersonDetails';
import { IconButton } from './ui';

/**
 * One right-hand dock with two tabs: the full change list, and the details of
 * whoever is selected. Slides fully out of the way when closed.
 */
export function SidePanel() {
  const open = useAppStore((s) => s.panelOpen);
  const tab = useAppStore((s) => s.panelTab);
  const total = useAppStore((s) => s.summary.total);
  const scenarioName = useAppStore((s) => s.scenario?.name);
  const setPanelTab = useAppStore((s) => s.setPanelTab);
  const togglePanel = useAppStore((s) => s.togglePanel);

  return (
    <aside
      aria-label="Scenario panel"
      className={cn(
        'z-20 h-full shrink-0 overflow-hidden border-l border-[var(--color-line)] bg-[var(--color-surface)]',
        'transition-[width] duration-300 ease-[var(--ease-org)]',
        /*
         * Mobile first, and deliberately so: a base `w-[340px]` with a
         * `max-sm:w-full` override ties on specificity, so which one wins comes
         * down to the order Tailwind happens to emit them in. Going upwards
         * through the breakpoints is the direction the cascade is built for.
         *
         * On a phone a 340px dock would leave the chart about 35px wide, so the
         * panel overlays instead: the chart keeps the full viewport underneath
         * and is still where you left it when the panel closes.
         */
        'absolute inset-y-0 right-0 shadow-[var(--shadow-panel)]',
        'sm:relative sm:inset-auto sm:shadow-none',
        open ? 'w-full sm:w-[340px]' : 'w-0 sm:w-0',
      )}
    >
      <div className="flex h-full w-full flex-col sm:w-[340px]">
        <div className="flex h-11 shrink-0 items-center gap-1 border-b border-[var(--color-line)] px-2">
          <Tab active={tab === 'changes'} onClick={() => setPanelTab('changes')}>
            <GitCompareArrows size={13} />
            Changes
            {total > 0 ? (
              <span className="rounded-full bg-[var(--color-brand-fill)] px-1.5 text-[10px] font-bold text-white">
                {total}
              </span>
            ) : null}
          </Tab>
          <Tab active={tab === 'history'} onClick={() => setPanelTab('history')}>
            <History size={13} />
            History
          </Tab>
          <Tab active={tab === 'details'} onClick={() => setPanelTab('details')}>
            <User size={13} />
            Details
          </Tab>
          <div className="flex-1" />
          <IconButton label="Close panel" onClick={togglePanel}>
            <PanelRightClose size={15} />
          </IconButton>
        </div>

        {tab === 'changes' ? (
          <div className="min-h-0 flex-1">
            <p className="truncate border-b border-[var(--color-line)] px-3 py-2 text-[11px] text-[var(--color-ink-3)]">
              Proposed changes in{' '}
              <span className="font-medium text-[var(--color-ink-2)]">{scenarioName}</span>
            </p>
            <div className="h-[calc(100%-33px)]">
              <ChangesList />
            </div>
          </div>
        ) : tab === 'history' ? (
          <div className="min-h-0 flex-1">
            <HistoryBar />
            <div className="h-[calc(100%-37px)]">
              <HistoryList />
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1">
            <PersonDetails />
          </div>
        )}
      </div>
    </aside>
  );
}

/**
 * Undo and redo live with the history rather than in the toolbar.
 *
 * They act on the log, not on this tab — after Claude has edited the same plan,
 * "undo" means whatever the server recorded last, which may not be yours. Next
 * to the list that says so, that is obvious; in a toolbar it would be a trap.
 */
function HistoryBar() {
  const history = useAppStore((s) => s.history);
  const undo = useAppStore((s) => s.undo);
  const redo = useAppStore((s) => s.redo);

  return (
    <div className="flex h-[37px] shrink-0 items-center gap-1 border-b border-[var(--color-line)] px-2">
      <IconButton label="Undo the last change" disabled={!history?.canUndo} onClick={() => void undo()}>
        <RotateCcw size={14} />
      </IconButton>
      <IconButton
        label="Redo the last undone change"
        disabled={!history?.canRedo}
        onClick={() => void redo()}
      >
        <RotateCw size={14} />
      </IconButton>
      <p className="ml-1 truncate text-[11px] text-[var(--color-ink-3)]">
        Everything that happened to this plan
      </p>
    </div>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12.5px] font-medium transition-colors',
        active
          ? 'bg-[var(--color-surface-2)] text-[var(--color-ink)]'
          : 'text-[var(--color-ink-3)] hover:text-[var(--color-ink-2)]',
      )}
    >
      {children}
    </button>
  );
}
